import { MessageCircle, X } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useState, Suspense, useEffect } from 'react';
import { useUnreadMessages } from '@/hooks/useUnreadMessages';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { lazyRetry } from '@/lib/lazyRetry';

// Este import tinha a própria cópia da recuperação de chunk, com uma chave de
// sessionStorage diferente da usada em App.tsx — as duas achavam que eram a
// primeira a recarregar e podiam disparar reloads em duplicidade, além de
// lançar o erro mesmo depois de já ter pedido o reload. lazyRetry é o mesmo
// mecanismo usado nas rotas; ver src/lib/chunkRecarrega.ts.
const ChatInterno = lazyRetry(() => import('@/pages/ChatInterno'));

// Dispensar vale para a aba: o sessionStorage mantém o botão fora do caminho
// enquanto a pessoa navega entre as telas (e sobrevive ao F5, que é o que ela
// espera de quem só queria ver o rodapé da tabela), e ele volta sozinho na
// próxima vez que o sistema for aberto. Em localStorage o contador de não lidas
// ficaria escondido por tempo indeterminado, sem lugar óbvio para reativar.
const CHAVE_DISPENSADO = 'chatcontar:botao-dispensado';

export default function FloatingChatButton() {
  const location = useLocation();
  const unreadCount = useUnreadMessages();
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [dispensado, setDispensado] = useState(() => {
    try {
      return sessionStorage.getItem(CHAVE_DISPENSADO) === '1';
    } catch {
      // Navegador com armazenamento bloqueado: o botão simplesmente aparece.
      return false;
    }
  });

  // Hide on chat page and login/reset pages
  const hidden =
    location.pathname === '/chat' ||
    location.pathname === '/login' ||
    location.pathname === '/forgot-password' ||
    location.pathname === '/reset-password' ||
    location.pathname.startsWith('/evento/') ||
    location.pathname === '/tv' ||
    // Página pública para parceira externa: não há usuário logado para conversar.
    location.pathname === '/agenda-certificados';

  // Lock page scroll while chat modal is open
  useEffect(() => {
    if (!open) return;
    const scrollY = window.scrollY;
    const prevBodyOverflow = document.body.style.overflow;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevBodyPosition = document.body.style.position;
    const prevBodyTop = document.body.style.top;
    const prevBodyWidth = document.body.style.width;

    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';

    return () => {
      document.body.style.overflow = prevBodyOverflow;
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.style.position = prevBodyPosition;
      document.body.style.top = prevBodyTop;
      document.body.style.width = prevBodyWidth;
      window.scrollTo(0, scrollY);
    };
  }, [open]);

  // Esconder aqui não garante uma cópia só do chat no ar. Sair de /chat para uma
  // tela que ainda não foi baixada cai no Suspense do App, e o React esconde a
  // rota anterior em vez de desmontá-la: a página de chat segue viva, sem rodar
  // limpeza, enquanto este painel volta no mesmo instante — ele continua montado
  // desde a primeira abertura, logo abaixo. As duas convivem até o arquivo da
  // tela nova chegar. Em 04/09/2026 essa janela derrubou a tela, porque os canais
  // de realtime do chat tinham nome fixo; ver src/lib/canalRealtime.ts.
  if (hidden) return null;

  const handleOpen = () => {
    setMounted(true);
    setOpen(true);
  };

  const dispensar = () => {
    setDispensado(true);
    setOpen(false);
    try {
      sessionStorage.setItem(CHAVE_DISPENSADO, '1');
    } catch {
      // Sem armazenamento, some só até a próxima troca de tela — melhor que nada.
    }
  };

  return (
    <>
      {!dispensado && (
        <div className="fixed bottom-6 right-6 z-50 print:hidden group">
          <motion.button
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleOpen}
            className="relative h-14 w-14 rounded-full bg-primary text-primary-foreground shadow-lg flex items-center justify-center hover:bg-primary/90 transition-colors"
            title="ChatContar"
          >
            <MessageCircle className="h-6 w-6" />
            <AnimatePresence>
              {unreadCount > 0 && (
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  exit={{ scale: 0 }}
                  className="absolute -top-1 -right-1 bg-destructive text-destructive-foreground text-xs font-bold rounded-full h-5 min-w-[20px] px-1 flex items-center justify-center"
                >
                  {unreadCount > 99 ? '99+' : unreadCount}
                </motion.span>
              )}
            </AnimatePresence>
          </motion.button>

          {/* Onde há mouse, o "x" só aparece ao passar por cima, para não
              competir com o contador de não lidas; onde não há hover (toque),
              fica sempre visível — senão não haveria como dispensar. */}
          <button
            onClick={dispensar}
            aria-label="Fechar o botão do ChatContar"
            title="Fechar (volta na próxima vez que você abrir o sistema)"
            className="absolute -top-1 -left-1 h-6 w-6 rounded-full bg-background text-muted-foreground border border-border shadow-sm flex items-center justify-center opacity-0 transition-opacity hover:bg-muted hover:text-foreground focus-visible:opacity-100 group-hover:opacity-100 [@media(hover:none)]:opacity-100"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* Fechar só esconde; o painel só desmonta quando a rota vira /chat. */}
      {mounted && (
        <div
          className={`fixed inset-0 z-[100] print:hidden ${open ? '' : 'hidden'}`}
          aria-hidden={!open}
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setOpen(false)}
          />
          {/* Modal */}
          <div className="absolute inset-0 flex items-center justify-center p-4 pointer-events-none">
            <div className="relative w-full max-w-6xl h-[90vh] bg-background rounded-xl shadow-2xl overflow-hidden pointer-events-auto flex flex-col">
              <button
                onClick={() => setOpen(false)}
                className="absolute top-3 right-3 z-10 h-8 w-8 rounded-full bg-background/80 hover:bg-muted flex items-center justify-center transition-colors"
                title="Fechar"
              >
                <X className="h-4 w-4" />
              </button>
              <div className="flex-1 min-h-0 overflow-hidden p-4">
                <Suspense
                  fallback={
                    <div className="flex items-center justify-center h-full">
                      <Loader2 className="h-6 w-6 animate-spin text-primary-texto" />
                    </div>
                  }
                >
                  <ChatInterno embedded />
                </Suspense>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
